import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TableMetadata, ColumnSortEnum, TableStateWithFilters, TableState, SortState, TablePaginationProps } from "@king-ict/eupisi-ui/components";
import qs from "qs";
import { useHistory, useLocation } from "react-router";

import { MasterDetailTableProps } from "./MasterDetailTable";

type Unarray<T> = T extends Array<infer U> ? U : T;

type FilterTypeDefinition<T> = {
    [K in keyof T]: T[K] extends any[] | [] | undefined
      ? FilterTypeDefinition<Unarray<T[K] | undefined>>[] | undefined
      : T[K] extends object | undefined
      ? FilterTypeDefinition<T[K] | undefined>
      : "string" | "number" | "boolean" | ((v: string) => any);
  };

type EmptyFilterObject = {};

type PageSize = 5 | 10 | 25 | 50 | 100;

type DefaultSort = {
    sortBy?: number;
    order?: ColumnSortEnum;
  };

interface useMasterDetailTableProps<T>{
    masterMetadata: TableMetadata<any>;
    masterData: any[];
    detailMetadata: TableMetadata<any>;    
    detailPropertyColumn: string;
    totalItems: number;
    pagination?: boolean;
    paginationPage?: number;
    paginationPageSize?: PageSize;
    paginationPagesVisible?: number;
    noHover?: boolean;    
    filtersObjectData?: T;
    useQueryParams?: boolean;
    setFiltersFromQueryParams?: (filters: T) => void;
    filtersTypeDefinition?: FilterTypeDefinition<T>;
    defaultSort?: DefaultSort;
}

export function useMasterDetailTable<FilterType = EmptyFilterObject>({
    masterMetadata,
    masterData,
    detailMetadata,    
    detailPropertyColumn,
    totalItems,
    filtersObjectData,
    setFiltersFromQueryParams,
    filtersTypeDefinition,
    pagination = false,
    useQueryParams = false,
    paginationPage = 1,
    paginationPageSize = 10,
    paginationPagesVisible = 5,
    noHover = false,
    defaultSort
}: useMasterDetailTableProps<FilterType>): [
    MasterDetailTableProps, 
    TableStateWithFilters<FilterType>,
    (filters: FilterType) => void
]{
    const initTableState = {
        page: pagination ? paginationPage : undefined,
        pageSize: pagination ? paginationPageSize : undefined,
        order:
          defaultSort && defaultSort.sortBy
            ? defaultSort.order
              ? defaultSort.order
              : ColumnSortEnum.ASC
            : undefined,
        sortBy: defaultSort && defaultSort.sortBy ? defaultSort.sortBy : undefined,
      } as TableState;

      const history = useHistory();
      const location = useLocation();
      const [tableState, setTableState] = useState<TableState>(initTableState);

      const [totalItemsCount, setTotalItemsCount] = useState(totalItems);

      function getStateFromQueryParams() {
        const search = location.search.substring(1);
        const parsedQuery = qs.parse(search);
    
        if (Object.keys(parsedQuery).length > 0) {
          const newTableState = converToTypedObject<TableState>(parsedQuery, {
            page: Number,
            pageSize: (v: string) => getPageSize(v),
            order: Number,
            sortBy: Number,
          });
    
          let newFiltersState;
    
          if (filtersTypeDefinition) {
            newFiltersState = converToTypedObject<FilterType>(
              parsedQuery,
              filtersTypeDefinition
            );
          }
    
          return {
            queryTableState: newTableState,
            queryFiltersState: newFiltersState,
          };
        }
        return {
          queryTableState: undefined,
          queryFiltersState: undefined,
        };
      }

      const sortBy = useCallback(
        (column: number) => {const defaultOrder = defaultSort && defaultSort.order ? defaultSort.order : undefined;
          const defaultSortBy = defaultSort && defaultSort.sortBy ? defaultSort.sortBy : undefined;
      
          //by convention if the table is currently ordered in desceding order on the column that is clicked
          //the sort should be reset to the default sort - this is the agreed functionality
      
          setTableState((prev) => {
            var newSortBy: number | undefined = undefined;
            //if the current sort by is on this column and descending order, switch to default sort
            if (prev.sortBy === column && prev.order === ColumnSortEnum.DESC) {
              newSortBy = defaultSortBy;
            }
            else {
              newSortBy = column;
            }
      
            var newOrder: ColumnSortEnum | undefined = undefined;
            //if the table is currently not sorted on the clicked column, then the new order is ASC
            if (prev.sortBy !== column) {
              newOrder = ColumnSortEnum.ASC
            } 
            //the table is already sorted on clicked column and order is ASC then we switch to DESC
            else if (prev.order === ColumnSortEnum.ASC) {
              newOrder = ColumnSortEnum.DESC
            }
            //the table is already sorted on clicked column and order is DESC then we switch to default sort
            //unless the default sort is also on the clicked column and the default sort order is also DESC, 
            //then we just switch to ASC
            else if (prev.order === ColumnSortEnum.DESC) {
              if (column !== defaultSortBy || defaultOrder !== ColumnSortEnum.DESC) {
                newOrder = defaultOrder;
              } else {
                newOrder = ColumnSortEnum.ASC
              }
            }
            return {
              ...prev,
              page: 1,
              sortBy: newSortBy,
              order: newOrder
          }});
        },
        [defaultSort]
      );

      const { queryTableState, queryFiltersState } = getStateFromQueryParams();
      const [filtersState, setFiltersState] = useState<FilterType>(
        useQueryParams && queryFiltersState
          ? queryFiltersState
          : filtersObjectData || ({} as FilterType)
      );
        
      useEffect(() => {        
        setTotalItemsCount(totalItems);
      }, [totalItems]);

      const [totalPageCount, setTotalPageCount] = useState(
        tableState.pageSize ? Math.ceil(totalItems / tableState.pageSize) : 1
      );

      useEffect(() => {        
        setTotalPageCount(
          tableState.pageSize ? Math.ceil(totalItems / tableState.pageSize) : 1
        );
      }, [tableState.pageSize, totalItems]);

      const canPreviousPage = tableState.page != null && tableState.page > 1;
      const canNextPage = tableState.page != null && tableState.page < totalPageCount;

      const previousPage = () => {
        setTableState((prev: any) => ({
          ...prev,
          page: prev.page != null && prev.page > 1 ? prev.page - 1 : prev.page,
        }));
      };
    
      const nextPage = useCallback(() => {
        setTableState((prev: any) => ({
          ...prev,
          page:
            prev.page != null && prev.page < totalPageCount
              ? prev.page + 1
              : prev.page,
        }));
      }, [totalPageCount]);

      const gotoPage = (page: number) => {
        setTableState((prev: any) => ({
          ...prev,
          page: page,
        }));
      };

      const setPageSize = (pageSize: number) => {
        setTableState((prev) => ({
          ...prev,
          pageSize: pageSize as PageSize,
          page: 1,
        }));
      };

      const setFilters = useCallback((filters: FilterType) => {
        gotoPage(1);
        setFiltersState(filters);
      }, []);


      const paginationProps = useMemo<TablePaginationProps>(
        () => ({
          page: tableState.page || 1,
          pageSize: tableState.pageSize || 10,
          pagesVisible: paginationPagesVisible,
          totalPageCount,
          canPreviousPage,
          canNextPage,
          previousPage,
          nextPage,
          gotoPage,
          setPageSize,
          itemsCount: totalItemsCount,
        }),
        [
          canNextPage,
          canPreviousPage,
          nextPage,
          paginationPagesVisible,
          tableState.page,
          tableState.pageSize,
          totalItemsCount,
          totalPageCount,
        ]
      );

      const columnSort = useMemo<SortState>(
        () => ({
          column: tableState.sortBy,
          sortType: tableState.order,
        }),
        [tableState.order, tableState.sortBy]
      );
      
      const returnTableState = useMemo<TableStateWithFilters<FilterType>>(
        () => ({
          page: tableState.page,
          pageSize: tableState.pageSize,
          sortBy: tableState.sortBy,
          order: tableState.order,
          ...filtersState,
        }),
        [
          filtersState,
          tableState.order,
          tableState.page,
          tableState.pageSize,
          tableState.sortBy,
        ]
      );
      
      const masterDetailTableProps = useMemo<MasterDetailTableProps>(
        () => ({
          masterMetadata,
          masterData,
          detailMetadata,          
          detailPropertyColumn,
          pagination,
          paginationProps,
          columnSort: columnSort,
          noHover,          
          sortBy,
        }),
        [
          columnSort,
          masterData,
          masterMetadata,
          pagination,
          paginationProps,
          sortBy,
          noHover,
          detailMetadata,          
          detailPropertyColumn          
        ]
      );

      return [masterDetailTableProps, returnTableState, setFilters];
}

function getDataByType(filterType: string, value: string) {
    switch (filterType) {
      case "string":
        return value;
      case "number":
        return +value;
      case "boolean":
        return value === "true" ? true : value === "false" ? false : undefined;
    }
  }

function getPageSize(size: string): PageSize {
    const defaultSize: PageSize = 10;
    const pageSize = +size;
    if (
      pageSize !== 5 &&
      pageSize !== 10 &&
      pageSize !== 25 &&
      pageSize !== 50 &&
      pageSize !== 100
    ) {
      return defaultSize;
    }
    return pageSize;
  }

function converToTypedObject<T>(
    v: any,
    d: FilterTypeDefinition<T>,
    dIsArray?: boolean
  ): T {
    let ret: any = dIsArray ? [] : {};
    const definition = d as any;
    for (let key in v) {
      if (definition[key] !== undefined || dIsArray) {
        const definitionToUse = dIsArray
          ? definition[key]
            ? definition[key]
            : definition[0]
          : definition[key];
        const type = typeof (dIsArray
          ? definition[key]
            ? definition[key]
            : definition[0]
          : definition[key]);
        if (type === "object") {
          if (Array.isArray(v[key])) {
            const val = converToTypedObject(v[key], definitionToUse, true);
            ret[key] = val;
          } else {
            const val = converToTypedObject(v[key], definitionToUse);
            ret[key] = val;
          }
        } else {
          let val;
          if (type === "function") {
            val = definition[key](v[key]);
          } else {
            const filterType = definition[key];
            val = getDataByType(filterType, v[key]);
          }
          if (typeof val !== "undefined") {
            ret[key] = val;
          }
        }
      }
    }
    return ret;
  }